import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SelfRegisteredBadge from "@/components/SelfRegisteredBadge";
import { Wand2, Loader2, ArrowLeft, ArrowRight, SkipForward, Check, X, Send } from "lucide-react";
import { JourneyLine } from "../JourneyLine";
import { EmailSendConfirm } from "./EmailSendConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smart-leads`;
const MAX = 320;
const n = (t) => [...(t || "")].length;
const Chip = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold rounded-full border px-2 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
    {label} {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
  </span>
);

// Current lead in focus mode: SMS or email draft editor + Back / Skip / Switch / Save & next.
export const FocusEditor = forwardRef(({ lead, d, pos, channel, onBack, onSkip, onSaved }, ref) => {
  const [text, setText] = useState(d?.text || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [orig, setOrig] = useState({ subject: "", body: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const email = channel === "email";

  useEffect(() => { setText(d?.text || ""); }, [d?.text, lead.id]);
  useEffect(() => {
    setNote("");
    if (!email) return;
    setSubject(""); setBody(""); setBusy("load");
    axios.post(`${API}/draft`, { lead_id: lead.id, channel: "email" })
      .then((r) => { setSubject(r.data.subject || ""); setBody(r.data.body || ""); setOrig({ subject: r.data.subject || "", body: r.data.body || "" }); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Email draft failed")).finally(() => setBusy(""));
  }, [lead.id, email]);

  const rewrite = async () => {
    setBusy("rewrite");
    try {
      const r = (await axios.post(`${API}/draft`, { lead_id: lead.id, channel, regenerate: true, note: note.trim() })).data;
      if (email) { setSubject(r.subject || ""); setBody(r.body || ""); } else setText(r.text || "");
      setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Rewrite failed"); } finally { setBusy(""); }
  };
  const valid = email ? subject.trim() && body.trim() : text.trim() && n(text) <= MAX;
  const save = async () => {
    if (!valid || busy) return;
    setBusy("save");
    try {
      let patch = {};
      if (email) {
        if (subject.trim() !== orig.subject.trim() || body.trim() !== orig.body.trim())
          await axios.put(`${API}/draft`, { lead_id: lead.id, channel: "email", subject: subject.trim(), body: body.trim() });
      } else if (text.trim() !== (d?.text || "").trim()) {
        await axios.put(`${API}/draft`, { lead_id: lead.id, text: text.trim() });
        patch = { text: text.trim(), edited: true };
      }
      const r = (await axios.post(`${API}/drafts/review`, { lead_id: lead.id, channel })).data;
      onSaved({ ...patch, reviewed_by: r.reviewed_by, reviewed_at: r.reviewed_at, reviewed_channel: channel }, !!patch.edited);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };
  useImperativeHandle(ref, () => ({ save }));
  const toOutlook = async () => {
    setBusy("outlook");
    try {
      const r = (await axios.post(`${API}/email/outlook-review`, { lead_id: lead.id, subject: subject.trim(), body: body.trim() })).data;
      toast.success(`Draft sent to ${r.sent_to} for review`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send to Outlook"); } finally { setBusy(""); }
  };

  const loading = !d || busy === "load";
  return (
    <div className="rounded-2xl border-2 border-indigo-500 shadow-lg p-4 space-y-3 bg-white" data-testid="focus-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">{pos}</span>
        <b className="text-lg text-slate-900" data-testid="focus-editor-name">{lead.name || "Unknown"}</b>
        {lead.self_registered && <SelfRegisteredBadge size="sm" />}
        <span className="text-xs text-slate-400">Age {lead.days_since_created ?? 0}d · {lead.outreach_count ?? 0} contacts</span>
      </div>
      {d && !d.error && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {lead.service && <span className="text-purple-700 mr-1">{lead.service}</span>}
          <Chip ok={d.sms_ok} label="SMS" /><Chip ok={d.email_ok} label="Email" />
          <span className="text-slate-500">· {d.sms_why}</span>
        </div>
      )}
      <JourneyLine j={d?.journey} tid="focus-journey" />
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-6"><Loader2 className="w-4 h-4 animate-spin" />{email ? "Writing a personal email…" : "Writing draft…"}</div>
      ) : d.error ? <div className="text-sm text-rose-600">Draft failed: {d.error}</div>
        : d.skip ? <div className="text-sm text-slate-600 py-4" data-testid="focus-journey-skip">{d.journey?.action === "remove" ? "Already booked in Gingr — marked Booked and removed from the queue." : "No follow-up needed — Gingr is already sending their reminders."} Click Skip.</div>
        : !d.sms_ok && !d.email_ok ? <div className="text-sm text-slate-500 py-4">Can't text or email this lead — call them, then Skip.</div>
        : email ? (
          <div className="space-y-2">
            <div className="text-[11px] text-slate-500">To: <b>{d.email}</b></div>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} placeholder="Subject" data-testid="focus-email-subject" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} maxLength={3000} data-testid="focus-email-body" />
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700" disabled={!valid || !!busy} onClick={toOutlook} data-testid="focus-outlook-review-btn">
              {busy === "outlook" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Send to my Outlook for review</Button>
            <span className="ml-2 inline-block"><EmailSendConfirm leadId={lead.id} subject={subject.trim()} body={body.trim()} disabled={!valid || !!busy}
              onSent={() => axios.post(`${API}/drafts/review`, { lead_id: lead.id, channel: "email" }).then((r) => onSaved({ reviewed_by: r.data.reviewed_by, reviewed_at: r.data.reviewed_at, reviewed_channel: "email" }, false))} /></span>
          </div>
        ) : (
          <>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} className={n(text) > MAX ? "border-rose-400" : ""} data-testid="focus-sms-text" />
            <span className={`text-xs font-semibold ${n(text) > MAX ? "text-rose-600" : "text-slate-400"}`}>{n(text)}/{MAX}</span>
          </>
        )}
      {d && !d.error && !d.skip && (d.sms_ok || d.email_ok) && !loading && (
        <div className="flex gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell AI what to change… e.g. mention the 3-dog discount" className="h-8 text-xs" data-testid="focus-rewrite-note" />
          <Button size="sm" variant="outline" className="h-8 shrink-0 border-amber-300 text-amber-700" disabled={!!busy} onClick={rewrite} data-testid="focus-rewrite-btn">
            {busy === "rewrite" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}{note.trim() ? "Rewrite" : "New draft"}
          </Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={onBack} disabled={pos === 1} data-testid="focus-back-btn"><ArrowLeft className="w-3.5 h-3.5 mr-1" />Back</Button>
        <Button size="sm" variant="outline" onClick={onSkip} data-testid="focus-skip-btn"><SkipForward className="w-3.5 h-3.5 mr-1" />Skip</Button>
        <Button size="sm" className="ml-auto bg-indigo-600 hover:bg-indigo-700" disabled={!valid || loading || !!busy} onClick={save} data-testid="focus-save-next-btn">
          {busy === "save" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save &amp; next <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
});
